import { useState, useRef, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { importApi } from '../api/client';
import { T } from '../components/ui';

type Step = 'drop' | 'mapping' | 'validating' | 'preview' | 'importing' | 'done';

interface UploadResult {
  job_id: string; filename: string; total_rows: number;
  headers: string[]; column_mapping: Record<string, string>;
  preview_rows: Record<string, string>[];
  unmapped_columns: string[];
}

interface ValidationResult {
  job_id: string; total_rows: number; valid_rows: number; error_rows: number;
  errors: { row: number; field: string; message: string }[];
  ready_to_import: boolean;
}

const TF_FIELDS = [
  'name', 'brand', 'category', 'barcode', 'sku',
  'origin_description', 'price_usd', 'retailer_slug',
  'supplier_name', 'certifications', 'img_emoji',
];

const FIELD_LABELS: Record<string, string> = {
  name: 'Product Name *', brand: 'Brand', category: 'Category',
  barcode: 'Barcode / GTIN', sku: 'SKU', origin_description: 'Origin',
  price_usd: 'Price (USD)', retailer_slug: 'Retailer Slug',
  supplier_name: 'Supplier Name', certifications: 'Certifications',
  img_emoji: 'Emoji',
};

const STATUS_COLORS: Record<string, string> = {
  done: T.sage, failed: T.crimson, importing: T.copper,
  preview: T.sapphire, rolled_back: T.dim,
};

export default function ImportPage() {
  const [step, setStep]           = useState<Step>('drop');
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upload, setUpload]       = useState<UploadResult | null>(null);
  const [mapping, setMapping]     = useState<Record<string, string>>({});
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [running, setRunning]     = useState(false);
  const [result, setResult]       = useState<{ imported: number; skipped: number } | null>(null);
  const [error, setError]         = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: jobs, refetch: refetchJobs } = useApi(() => importApi.list(), [step]);

  const handleFile = useCallback(async (file: File) => {
    const ext = file.name.match(/\.(csv|xlsx|xls)$/i);
    if (!ext) { setError('Only CSV and Excel (.xlsx/.xls) files are supported'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('File too large (max 10 MB)'); return; }

    setError('');
    setUploading(true);
    setStep('mapping');
    try {
      const res = await importApi.upload(file);
      setUpload(res);
      setMapping(res.column_mapping);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
      setStep('drop');
    } finally {
      setUploading(false);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const runValidation = async () => {
    if (!upload) return;
    setStep('validating');
    setError('');
    try {
      const res = await importApi.confirm(upload.job_id, mapping);
      setValidation(res);
      setStep('preview');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
      setStep('mapping');
    }
  };

  const runImport = async () => {
    if (!upload) return;
    setRunning(true);
    setStep('importing');
    try {
      const res = await importApi.run(upload.job_id);
      setResult({ imported: res.imported, skipped: res.skipped });
      setStep('done');
      refetchJobs?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
      setStep('preview');
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setStep('drop'); setUpload(null); setMapping({});
    setValidation(null); setResult(null); setError('');
  };

  const rollback = async (jobId: string) => {
    if (!confirm('Roll back this import? All imported products will be removed.')) return;
    try {
      const res = await importApi.rollback(jobId);
      alert(`Rolled back ${res.removed} productsApi.`);
      refetchJobs?.();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Rollback failed');
    }
  };

  const inputStyle: React.CSSProperties = {
    background: T.panel, border: `1px solid ${T.border}`,
    borderRadius: 6, color: T.text, fontSize: 12, outline: 'none',
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>
        Bulk Import
      </h1>
      <p style={{ fontSize: 13, color: T.sub, margin: '0 0 28px' }}>
        Upload a CSV or Excel file to import products in bulk
      </p>

      {/* ── STEP 1: Drop zone ── */}
      {step === 'drop' && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? T.copper : T.border}`,
            borderRadius: 16, padding: '60px 32px',
            textAlign: 'center', cursor: 'pointer',
            background: dragging ? `${T.copper}08` : T.panel,
            transition: 'all 0.15s',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>📂</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>
            Drop your file here, or click to browse
          </div>
          <div style={{ fontSize: 13, color: T.sub }}>
            CSV, XLSX, or XLS · Max 10 MB · Up to 50,000 rows
          </div>
          {error && (
            <div style={{ marginTop: 16, fontSize: 13, color: T.crimson }}>{error}</div>
          )}
          <input
            ref={fileRef} type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={onFileInput}
          />
        </div>
      )}

      {/* ── STEP 2: Column mapping ── */}
      {step === 'mapping' && upload && (
        <div>
          {uploading ? (
            <div style={{ textAlign: 'center', padding: 60, color: T.sub }}>
              Parsing <strong>{upload?.filename || 'file'}</strong>…
            </div>
          ) : (
            <>
              <div style={{
                background: T.panel, border: `1px solid ${T.border}`,
                borderRadius: 12, padding: 24, marginBottom: 20,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{upload.filename}</div>
                    <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>
                      {upload.total_rows.toLocaleString()} rows · {upload.headers.length} columns detected
                    </div>
                  </div>
                  {upload.unmapped_columns.length > 0 && (
                    <div style={{ fontSize: 11, color: T.amber, background: `${T.amber}15`, padding: '4px 10px', borderRadius: 20, alignSelf: 'flex-start' }}>
                      {upload.unmapped_columns.length} unmapped column{upload.unmapped_columns.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px 16px', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your column</div>
                  <div />
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Maps to</div>

                  {upload.headers.map(col => (
                    <>
                      <div key={`col-${col}`} style={{ fontSize: 13, color: T.text, padding: '4px 10px', background: T.card, borderRadius: 6 }}>
                        {col}
                      </div>
                      <div key={`arrow-${col}`} style={{ color: T.dim, textAlign: 'center' }}>→</div>
                      <select
                        key={`sel-${col}`}
                        value={mapping[col] || ''}
                        onChange={e => setMapping(m => ({ ...m, [col]: e.target.value }))}
                        style={{ ...inputStyle, padding: '5px 8px', cursor: 'pointer', width: '100%' }}
                      >
                        <option value="">— skip —</option>
                        {TF_FIELDS.map(f => (
                          <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>
                        ))}
                      </select>
                    </>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {upload.preview_rows.length > 0 && (
                <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Preview (first 5 rows)</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {(Object.values(mapping).filter(Boolean) as string[]).map(f => (
                            <th key={f} style={{ textAlign: 'left', padding: '4px 10px', color: T.sub, borderBottom: `1px solid ${T.border}` }}>
                              {FIELD_LABELS[f] || f}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {upload.preview_rows.map((row, i) => (
                          <tr key={i}>
                            {(Object.values(mapping).filter(Boolean) as string[]).map(f => (
                              <td key={f} style={{ padding: '5px 10px', color: T.text, borderBottom: `1px solid ${T.border}` }}>
                                {(row as Record<string, string>)[f] || <span style={{ color: T.dim }}>—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {error && <div style={{ fontSize: 13, color: T.crimson, marginBottom: 12 }}>{error}</div>}

              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={reset} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8, color: T.sub, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  onClick={runValidation}
                  disabled={!Object.values(mapping).includes('name')}
                  style={{
                    padding: '10px 28px', background: Object.values(mapping).includes('name') ? T.copper : T.dim,
                    border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Validate →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STEP 3: Validating spinner ── */}
      {step === 'validating' && (
        <div style={{ textAlign: 'center', padding: 60, color: T.sub }}>
          <div style={{ fontSize: 13 }}>Validating {upload?.total_rows?.toLocaleString()} rows…</div>
        </div>
      )}

      {/* ── STEP 4: Validation preview ── */}
      {step === 'preview' && validation && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
            {[
              { label: 'Total rows',   value: validation.total_rows, color: T.text },
              { label: 'Valid rows',   value: validation.valid_rows, color: T.sage },
              { label: 'Error rows',   value: validation.error_rows, color: validation.error_rows > 0 ? T.crimson : T.dim },
            ].map(s => (
              <div key={s.label} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px 20px' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: T.sub, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {validation.errors.length > 0 && (
            <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20, maxHeight: 240, overflowY: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>
                Validation errors ({validation.error_rows} rows affected)
              </div>
              {validation.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: T.sub, padding: '4px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ color: T.crimson }}>Row {e.row}</span>
                  <span style={{ color: T.dim }}> · {e.field}</span>
                  {' — '}{e.message}
                </div>
              ))}
            </div>
          )}

          {error && <div style={{ fontSize: 13, color: T.crimson, marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setStep('mapping')} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8, color: T.sub, fontSize: 13, cursor: 'pointer' }}>
              ← Back
            </button>
            <button
              onClick={runImport}
              disabled={!validation.ready_to_import || running}
              style={{
                padding: '10px 28px',
                background: validation.ready_to_import ? T.copper : T.dim,
                border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Import {validation.valid_rows.toLocaleString()} products →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 5: Importing spinner ── */}
      {step === 'importing' && (
        <div style={{ textAlign: 'center', padding: 60, color: T.sub }}>
          <div style={{ fontSize: 13 }}>Importing products… This may take a moment.</div>
        </div>
      )}

      {/* ── STEP 6: Done ── */}
      {step === 'done' && result && (
        <div style={{ background: T.panel, border: `1px solid ${T.sage}40`, borderRadius: 16, padding: 32, textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 6 }}>
            Import complete
          </div>
          <div style={{ fontSize: 14, color: T.sub, marginBottom: 20 }}>
            <span style={{ color: T.sage }}>{result.imported.toLocaleString()} products imported</span>
            {result.skipped > 0 && ` · ${result.skipped} skipped (duplicates)`}
          </div>
          <button onClick={reset} style={{ padding: '10px 24px', background: T.copper, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Import another file
          </button>
        </div>
      )}

      {/* ── Import history ── */}
      {jobs && jobs.length > 0 && (
        <div style={{ marginTop: 36 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 14 }}>
            Import history
          </div>
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {jobs.map((job, i) => (
              <div key={job.id} style={{
                display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 14,
                borderBottom: i < jobs.length - 1 ? `1px solid ${T.border}` : 'none',
              }}>
                <div style={{ fontSize: 18 }}>📄</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.filename}
                  </div>
                  <div style={{ fontSize: 11, color: T.sub, marginTop: 2 }}>
                    {new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {' · '}{job.created_by_name}
                    {job.imported_rows != null && ` · ${job.imported_rows.toLocaleString()} imported`}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 600,
                  background: `${STATUS_COLORS[job.status] || T.dim}20`,
                  color: STATUS_COLORS[job.status] || T.dim,
                }}>
                  {job.status}
                </span>
                {job.rollback_available && (
                  <button
                    onClick={() => rollback(job.id)}
                    style={{ padding: '4px 10px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 6, color: T.sub, fontSize: 11, cursor: 'pointer' }}
                  >
                    Rollback
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
